import React, { useEffect, useState } from "react";
import MetaTags from "react-meta-tags";
import { Link, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NasHeader from "../../layouts/_nas/NasHeader";
import NasFooter from "../../layouts/_nas/NasFooter";
import Breadcrumbs from "../../layouts/Breadcrumbs";
import axiosInstance from "../../../hooks/api";

const BRAND = "#032C65";

const NasPaymentSuccessPage = () => {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const location = useLocation();
  const [payment, setPayment] = useState(null);

  const query = new URLSearchParams(location.search);
  const sessionId = query.get("session_id");
  const paymentIntentId = query.get("payment_intent");
  const reference = paymentIntentId || sessionId;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let attempts = 0;
    let timer;

    const load = async () => {
      try {
        // If Elements redirected back after 3DS, sync status from Stripe first.
        if (paymentIntentId) {
          try {
            await axiosInstance.post(`/public/sacrament-payments/${token}/confirm`);
          } catch {
            // Webhook may still be catching up.
          }
        }
        const res = await axiosInstance.get(`/public/sacrament-payments/${token}`);
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        setPayment(payload);
        if (payload?.status !== "paid" && attempts < 5) {
          attempts += 1;
          timer = setTimeout(load, 1500);
        }
      } catch {
        // Confirmation UI still shows even if status poll fails.
      }
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, paymentIntentId]);

  const formatMoney = (amount, currency = "usd") => {
    try {
      return new Intl.NumberFormat(i18n.language === "es" ? "es-US" : "en-US", {
        style: "currency",
        currency: (currency || "usd").toUpperCase(),
      }).format(Number(amount || 0));
    } catch {
      return `$${Number(amount || 0).toFixed(2)}`;
    }
  };

  return (
    <>
      <MetaTags>
        <title>Holy Spirit Church | {t("_payment_success_title")}</title>
      </MetaTags>
      <NasHeader />
      <Breadcrumbs breadcrumb={{ pagename: t("_payment_success_title") }} />
      <section className="section" style={{ background: "#f7f8fa", paddingBottom: 80 }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 text-center">
              <div
                style={{
                  background: "#fff",
                  borderRadius: 18,
                  boxShadow: "0 10px 40px rgba(3,44,101,0.08)",
                  padding: "2.75rem 2rem",
                }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: "#e8f7ee",
                    color: "#1b6b3a",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 18,
                    fontSize: 32,
                  }}
                >
                  <i className="fal fa-check" />
                </div>
                <h3 style={{ color: BRAND, fontWeight: 800 }}>{t("_payment_success_title")}</h3>
                <p style={{ color: "#666", lineHeight: 1.7, marginBottom: 8 }}>
                  {t("_payment_success_body")}
                </p>
                {payment?.status === "paid" && (
                  <p style={{ color: BRAND, fontWeight: 700, fontSize: "1.15rem" }}>
                    {t("_payment_total")}: {formatMoney(payment.amount_total, payment.currency)}
                  </p>
                )}
                {reference && (
                  <p style={{ color: "#999", fontSize: 12, wordBreak: "break-all" }}>
                    {t("_payment_reference")}: {reference}
                  </p>
                )}
                <p style={{ color: "#777", fontSize: 14, marginTop: 12 }}>
                  {t("_payment_receipt_note")}
                </p>
                <div style={{ marginTop: 28 }}>
                  <Link to="/" className="btn" style={{ background: BRAND, color: "#fff" }}>
                    {t("_home")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <NasFooter />
    </>
  );
};

export default NasPaymentSuccessPage;
